import React, { useState, useEffect, useCallback } from 'react';
import { Application } from '../../types';
import { ConfirmationModal } from '../common/ConfirmationModal';

interface AddApplicationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (application: Omit<Application, 'id'>) => void;
}

export const AddApplicationModal: React.FC<AddApplicationModalProps> = ({
    isOpen,
    onClose,
    onSave
}) => {
    const [name, setName] = useState('');
    const [usage, setUsage] = useState('');
    const [customUsage, setCustomUsage] = useState('');
    const [location, setLocation] = useState('');
    const [customLocation, setCustomLocation] = useState('');
    const [tier, setTier] = useState(1);
    const [customer_facing_notes, setCustomerFacingNotes] = useState<string | null>('');
    const [internal_notes, setInternalNotes] = useState<string | null>('');
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

    // Function to reset form fields to initial values
    const resetForm = () => {
        setName('');
        setUsage('');
        setCustomUsage('');
        setLocation('');
        setCustomLocation('');
        setTier(1);
        setCustomerFacingNotes('');
        setInternalNotes('');
        setFormErrors({});
    };

    // Check if form has unsaved changes
    const hasUnsavedChanges = useCallback(() => {
        return name.trim() !== '' || 
               usage !== '' ||
               customUsage.trim() !== '' ||
               location !== '' ||
               customLocation.trim() !== '' ||
               tier !== 1 ||
               customer_facing_notes?.trim() !== '' ||
               internal_notes?.trim() !== '';
    }, [name, usage, customUsage, location, customLocation, tier, customer_facing_notes, internal_notes]);

    const handleCloseAttempt = useCallback(() => {
        if (hasUnsavedChanges()) {
            setIsCloseConfirmOpen(true);
        } else {
            onClose();
        }
    }, [hasUnsavedChanges, onClose]);

    const handleConfirmClose = () => {
        setIsCloseConfirmOpen(false);
        resetForm();
        onClose();
    };

    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleCloseAttempt();
            }
        };

        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, handleCloseAttempt]);

    // Reset form when modal is closed or opened
    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        // Update the corresponding state based on the input name
        switch (name) {
            case 'name':
                setName(value);
                break;
            case 'usage':
                setUsage(value);
                // Clear custom usage when a predefined option is selected
                if (value !== 'Custom') {
                    setCustomUsage('');
                }
                break;
            case 'customUsage':
                setCustomUsage(value);
                break;
            case 'location':
                setLocation(value);
                // Clear custom location when a predefined option is selected
                if (value !== 'Custom') {
                    setCustomLocation('');
                }
                break;
            case 'customLocation':
                setCustomLocation(value);
                break;
            case 'tier':
                setTier(Number(value));
                break;
            case 'customer_facing_notes':
                setCustomerFacingNotes(value);
                break;
            case 'internal_notes':
                setInternalNotes(value);
                break;
        }

        // Clear error when field is edited
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const errors: { [key: string]: string } = {};

        if (!name.trim()) {
            errors.name = 'Application name is required';
        }

        if (!usage) {
            errors.usage = 'Usage is required';
        } else if (usage === 'Custom' && !customUsage.trim()) {
            errors.customUsage = 'Custom usage is required';
        }

        if (!location) {
            errors.location = 'Location is required';
        } else if (location === 'Custom' && !customLocation.trim()) {
            errors.customLocation = 'Custom location is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm() && !isSubmitting) {
            setIsSubmitting(true);
            
            // Call onSave and handle the promise
            Promise.resolve(onSave({
                name,
                usage: usage === 'Custom' ? customUsage : usage,
                location: location === 'Custom' ? customLocation : location,
                tier,
                customer_facing_notes,
                internal_notes
            }))
            .finally(() => {
                resetForm(); // Reset form after successful submission
                setIsSubmitting(false);
                onClose();
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50" onClick={handleCloseAttempt}>
            <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold text-[#0F2A4F]">Add new application</h2>
                        <button
                            onClick={handleCloseAttempt}
                            className="rounded-full p-2 hover:bg-gray-100"
                            type="button"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Application Name
                            </label>
                            <input
                                type="text"
                                name="name"
                                placeholder="Enter application name"
                                value={name}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                            />
                            {formErrors.name && (
                                <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Usage Type
                            </label>
                            <div className="relative">
                                <select
                                    name="usage"
                                    value={usage}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border ${formErrors.usage ? 'border-red-500' : 'border-gray-300'} rounded-md appearance-none`}
                                >
                                    <option value="" disabled>Select Usage</option>
                                    <option value="CRM">CRM</option>
                                    <option value="ERP">ERP</option>
                                    <option value="HR">HR</option>
                                    <option value="Finance">Finance</option>
                                    <option value="Custom">Custom</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            {formErrors.usage && (
                                <p className="mt-1 text-sm text-red-500">{formErrors.usage}</p>
                            )}
                        </div>

                        {usage === 'Custom' && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Custom Usage
                                </label>
                                <input
                                    type="text"
                                    name="customUsage"
                                    placeholder="Enter custom usage type"
                                    value={customUsage}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border ${formErrors.customUsage ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                                />
                                {formErrors.customUsage && (
                                    <p className="mt-1 text-sm text-red-500">{formErrors.customUsage}</p>
                                )}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Location
                            </label>
                            <div className="relative">
                                <select
                                    name="location"
                                    value={location}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border ${formErrors.location ? 'border-red-500' : 'border-gray-300'} rounded-md appearance-none`}
                                >
                                    <option value="" disabled>Select Location</option>
                                    <option value="Azure/Global Use">Azure/Global Use</option>
                                    <option value="AWS">AWS</option>
                                    <option value="GCP">GCP</option>
                                    <option value="On-premise">On-premise</option>
                                    <option value="Hybrid">Hybrid</option>
                                    <option value="Saas">Saas</option>
                                    <option value="Custom">Custom</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                            {formErrors.location && (
                                <p className="mt-1 text-sm text-red-500">{formErrors.location}</p>
                            )}
                        </div>

                        {location === 'Custom' && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Custom Location
                                </label>
                                <input
                                    type="text"
                                    name="customLocation"
                                    placeholder="Enter custom location"
                                    value={customLocation}
                                    onChange={handleChange}
                                    className={`w-full px-3 py-2 border ${formErrors.customLocation ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                                />
                                {formErrors.customLocation && (
                                    <p className="mt-1 text-sm text-red-500">{formErrors.customLocation}</p>
                                )}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Application Tier (1-5)
                            </label>
                            <input
                                type="number"
                                name="tier"
                                min={1}
                                max={5}
                                value={tier}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        <div className="mb-4">
                            <textarea
                                name="customer_facing_notes"
                                placeholder="Enter Customer facing notes"
                                value={customer_facing_notes || ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
                                maxLength={2000}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                {(customer_facing_notes || '').length}/2000 chars remaining
                            </div>
                        </div>

                        <div className="mb-4">
                            <textarea
                                name="internal_notes"
                                placeholder="Enter Internal notes"
                                value={internal_notes || ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
                                maxLength={2000}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                {(internal_notes || '').length}/2000 chars remaining
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className={`px-4 py-2 border border-gray-300 rounded-md mr-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`px-4 py-2 bg-[#0F2A4F] text-white rounded-md flex items-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#204374]'}`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                        Creating...
                                    </>
                                ) : (
                                    'Create New Application'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
                         <ConfirmationModal
                 isOpen={isCloseConfirmOpen}
                 onClose={() => setIsCloseConfirmOpen(false)}
                 onConfirm={handleConfirmClose}
                 title="Unsaved Changes"
                 message="You have unsaved changes. Are you sure you want to leave this page? All your changes will be lost."
                 confirmButtonText="Leave Page"
                 cancelButtonText="Stay"
             />
        </div>
    );
};
